import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { collection, deleteField, doc } from "firebase/firestore";
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { LucideIcon } from "lucide-react";

import { EmptyState, LoadingCards } from "components/states";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Switch } from "components/ui/switch";
import { db } from "integrations/firebase/firestore";

import { CompanyField } from "../../business-units/CompanyField";

import { AdminCommandFeedback } from "../AdminCommandFeedback";
import { deleteEntityImage, uploadEntityImage } from "../entity-images";
import { useAdminCommand, type AdminCommandType } from "../use-admin-command";
import {
  deleteEntityDoc,
  useEntityWrites,
  type EntityFields,
} from "../use-entity-writes";
import {
  BulkDeleteDialog,
  BulkRowCheckbox,
  BulkSelectBar,
  BulkSelectButton,
} from "../bulk/BulkDelete";
import { useBulkDelete } from "../bulk/use-bulk-delete";

import { EntityImageField, type ImageShape } from "./EntityImageField";

export type FieldKind =
  | "text"
  | "textarea"
  | "number"
  | "switch"
  | "color"
  | "select"
  | "lookup"
  | "company"
  | "order"
  | "image";

/** One document another document may point at. */
export interface LookupOption {
  id: string;
  label: string;
  detail?: string;
  /** Values this choice can copy into other fields of the form, by name. */
  fills?: Record<string, string>;
}

/**
 * The choices for a `lookup` field, supplied by the caller because they are
 * documents rather than a fixed list the spec could hold.
 *
 * `unavailable` returns the reason a choice cannot be taken, or undefined when
 * it can. Speakers use it to refuse an attendee some other speaker already
 * links: the rules cannot check uniqueness across documents, so two speakers
 * can point at one person, and then that person has two cards in the speaker
 * list and neither appears in the agenda presenter picker. It is given the id
 * of the record being edited, or null on a create, so that a record is not
 * refused its own link after the organiser unlinks and thinks better of it.
 */
export interface LookupSource {
  options: LookupOption[];
  loading: boolean;
  error?: string;
  /** Shown when the search matches nothing, and when there is nothing at all. */
  empty: string;
  /** Shown in place of a linked value that matches no option once loaded:
   *  the record it pointed at has gone. Without it the raw value shows. */
  missing?: string;
  unavailable?: (
    option: LookupOption,
    editingId: string | null,
    /** The form as it stands, for a choice that depends on another field. */
    form: Record<string, string | number | boolean>,
  ) => string | undefined;
}

export interface FieldSpec {
  name: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
  placeholder?: string;
  help?: string;
  options?: { value: string; label: string }[];
  /** Sent only on create; the rules keep it out of every update key list. */
  createOnly?: boolean;
  /** Send the key even when blank. For a field the rules name in `hasAll` but
   *  validate with isOptionalBoundedString — absent is refused, empty is fine.
   *  Attendees are the case: `role` and `company` are both. */
  alwaysSend?: boolean;
  /** Blank removes the field instead of writing "". For a field whose rule is
   *  `== null || matches(...)`: an empty string is neither, so clearing it by
   *  sending "" is refused outright. Attendee `email` is the case. */
  clearWhenBlank?: boolean;
  /** Checked before writing, so a malformed value is a sentence rather than the
   *  unexplained permission-denied the matching rule would return. */
  pattern?: RegExp;
  patternMessage?: string;
  defaultValue?: string | number | boolean;
  /** `lookup` only: the other fields a chosen option may fill in. Only fields
   *  that are still blank are written, so a choice never overwrites typing. */
  fills?: string[];
  /** `lookup` only: a choice among documents of more than one collection,
   *  stored under one key per collection. Option ids are `<key>:<documentId>`;
   *  the chosen key gets the id, every other key listed here is removed, and
   *  the field's own name is never written. A track's champion is the case:
   *  a roster attendee (`championAttendeeId`) or a speaker
   *  (`championSpeakerId`), which the rules allow one of at a time. The
   *  second champion is the same with the `secondChampion…` keys. */
  storedAs?: string[];
  /** Written but never shown. For a key the rules require whose value is no
   *  longer a choice: information items must carry `kind`, and Q & A is now
   *  the only one there is. */
  hidden?: boolean;
  /** `image` only. The value is a download URL; a chosen file is uploaded to
   *  `events/{eventId}/{collection}/{id}/` on Save. The file it replaces is
   *  deleted by the backend once nothing shows it any more
   *  (functions/python/app/application/service/entity_image_cleanup.py), not
   *  here. `maxBytes` is the storage.rules bound for the path. */
  image?: { maxBytes: number; shape: ImageShape };
}

export interface EntitySpec<T> {
  collection: string;
  singular: string;
  plural: string;
  icon: LucideIcon;
  testId: string;
  fields: FieldSpec[];
  /** Fields the rules pin to request.time. */
  createTimestamps?: string[];
  updateTimestamps?: string[];
  /** When set, deletion is a command rather than a client delete. */
  deleteCommand?: { type: AdminCommandType; payloadKey: string };
  /** When set, a trigger strips this record's id out of the rest of the event
   *  after a plain client delete. Attendees are the case, and it matters to
   *  the organiser because re-adding the person lands them at a new id, so
   *  none of the stripped links come back. */
  deleteClearsReferences?: boolean;
  /** Offers a select mode with Delete selected. Only for a plain client
   *  delete: each row goes through the same delete its own button uses, one
   *  at a time, which a delete command with a watched outcome would need a
   *  different loop for. */
  bulkDelete?: boolean;
  /** When set, creation is a command rather than a client write, and the
   *  create payload becomes the command payload. Attendees are the case: the
   *  document id has to be sha256 of the email, rules cannot compute a hash,
   *  so the id is derived by the trigger and direct create is closed. */
  createCommand?: { type: AdminCommandType };
  primary: (item: T) => string;
  secondary?: (item: T) => string | undefined;
  toForm: (item: T) => Record<string, string | number | boolean>;
  /** Renders a search box and filters on these values, case-insensitively.
   *  Absent means no search — right for a handful of tracks, wrong for the
   *  hundreds of attendees a real event imports. */
  searchFields?: (item: T) => (string | undefined | null)[];
  /** An extra control on each row, left of edit and delete. Exists because
   *  attendee track assignment is a command, not a field: `trackId` is in no
   *  update key list, so it cannot be part of the form. */
  rowAction?: (item: T) => ReactNode;
  /** A control above the list, given the items currently VISIBLE — i.e. after
   *  the search filter. Only this component knows that set, which is the point:
   *  "apply to what you are looking at" is the useful bulk semantic. */
  bulkAction?: (visible: T[]) => ReactNode;
  /** Replaces the built-in form dialog for both add and edit. Rendered only
   *  while open, so it mounts fresh for each record. Venues are the case: the
   *  pin is a map and floor plans upload to Storage, neither of which is a
   *  field this form can hold, and the organiser asked for one venue editor
   *  instead of a form plus a second dialog behind a row button. */
  editor?: (props: {
    item: T | null;
    onClose: () => void;
    onSaved: () => void;
  }) => ReactNode;
  /** A small picture beside each row, so a logo or a photo can be checked
   *  without opening the record. */
  thumbnail?: (item: T) => string | undefined;
  /** Put one `lookup` field first and hold the rest of a new record back
   *  until it is used, or until the organiser asks to type the details.
   *  Speakers are the case: most speakers are on the roster, and linking one
   *  fills the rest of the form, so typing it all out is the fallback. */
  linkFirst?: { field: string; manualLabel: string };
  /** Shown above the fields of a NEW record's form. It fills fields by name
   *  through `onPick` and never saves anything itself. Attendees use it to
   *  search for somebody already on the app (AttendeeFinder); the form below
   *  stays as it was, for people who are not. */
  createFinder?: (props: {
    eventId: string;
    uid: string;
    disabled: boolean;
    onPick: (fields: Record<string, string>) => void;
  }) => ReactNode;
}

type FormValues = Record<string, string | number | boolean>;

/** What deleting an attendee does beyond removing the row. */
const CLEARS_REFERENCES =
  "They lose access to the event unless they are an event administrator, whose grant is kept and has to be removed in Governance. Sessions they present lose them as a presenter, any speaker profile pointing at them is unlinked, and their track has to be set again if they come back.";

function emptyForm(fields: FieldSpec[]): FormValues {
  const form: FormValues = {};
  for (const field of fields) {
    form[field.name] =
      field.defaultValue ??
      (field.kind === "switch"
        ? false
        : field.kind === "number" || field.kind === "order"
          ? 0
          : "");
  }
  return form;
}

/**
 * List-plus-dialog CRUD over one event content collection.
 *
 * The six content collections differ only in their fields and in how they are
 * deleted, so they share this shell. The differences that matter are declared
 * in the spec rather than branched on here: which fields exist, which are
 * create-only (the rules exclude them from every update key list), which
 * timestamps are pinned to `request.time`, and whether a delete is a direct
 * write or a command whose trigger has to sweep references first.
 */
export function EntityCrudSection<T extends { id: string }>({
  eventId,
  uid,
  spec,
  items,
  loading,
  error,
  reload,
  lookups,
}: {
  eventId: string;
  uid: string;
  spec: EntitySpec<T>;
  items: T[];
  loading: boolean;
  error?: string;
  reload: () => void;
  /** The choices for each `lookup` field, keyed by field name. They are
   *  documents of another collection, so the caller loads them. */
  lookups?: Record<string, LookupSource>;
}) {
  const writes = useEntityWrites(eventId, spec.collection);
  const command = useAdminCommand();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  const [form, setForm] = useState<FormValues>(() => emptyForm(spec.fields));
  const [invalid, setInvalid] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<T | null>(null);
  const [search, setSearch] = useState("");
  // Files chosen for `image` fields, by field name, waiting for Save.
  const [files, setFiles] = useState<Record<string, File>>({});
  const [uploading, setUploading] = useState(false);
  // True once the organiser chose to type a new record's details rather than
  // link it, or linked it; see `linkFirst`.
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Set when a file this form uploaded for a save that then failed could not
  // be deleted again. Shown under the refusal, so it is never silent.
  const [uploadLeftover, setUploadLeftover] = useState<string | null>(null);
  // A ref, not state: the effect below must not re-run when it changes, or a
  // successful command reloads twice.
  const awaitingCreate = useRef(false);

  const imageFields = spec.fields.filter((field) => field.kind === "image");

  const bulkEnabled = spec.bulkDelete === true && !spec.deleteCommand;
  const bulk = useBulkDelete({
    deleteOne: (id) => deleteEntityDoc(eventId, spec.collection, id),
    onDeleted: reload,
  });

  const commandBusy =
    command.state.status === "submitting" ||
    command.state.status === "watching";
  const commandError =
    command.state.status === "failed" ? command.state.error : null;

  useEffect(() => {
    if (command.state.status === "succeeded") {
      setPendingDelete(null);
      if (awaitingCreate.current) {
        awaitingCreate.current = false;
        setOpen(false);
      }
      reload();
    } else if (command.state.status === "failed") {
      // Leave the dialog open on failure. The refusal is about what was
      // typed — an address already on the roster, most often — so the form
      // has to still be there to correct.
      awaitingCreate.current = false;
    }
  }, [command.state.status, reload]);

  /**
   * Deletes files this form uploaded for a save that did not happen, so
   * nothing points at them. Only these: a file a document has pointed at is
   * the backend's to delete, once nothing shows it. Returns a sentence when
   * any could not be deleted, for the form to show.
   */
  async function discardUploads(paths: string[]): Promise<string | null> {
    if (paths.length === 0) return null;
    const results = await Promise.all(paths.map(deleteEntityImage));
    const failed = results.filter((deleted) => !deleted).length;
    if (failed === 0) return null;
    return failed === 1
      ? "The image uploaded for this save could not be deleted again. Nothing shows it, but anyone with its link can open it."
      : `${failed} images uploaded for this save could not be deleted again. Nothing shows them, but anyone with their links can open them.`;
  }

  function startCreate() {
    setEditing(null);
    setForm(emptyForm(spec.fields));
    setFiles({});
    setDetailsOpen(false);
    setInvalid(null);
    setUploadLeftover(null);
    // Clear a previous command's outcome, or the dialog opens showing the last
    // refusal as though it were about this attempt.
    command.reset();
    setOpen(true);
  }

  function startEdit(item: T) {
    setEditing(item);
    setForm({ ...emptyForm(spec.fields), ...spec.toForm(item) });
    setFiles({});
    setInvalid(null);
    setUploadLeftover(null);
    // Same reason as startCreate. A refused create left its message behind,
    // so editing an unrelated row opened showing "<address> is already an
    // attendee" under the fields.
    command.reset();
    setOpen(true);
  }

  /**
   * A select whose stored value is not among its options cannot be edited
   * without losing that value. Withdrawn attendees are the case: the status
   * dropdown offers pending and confirmed only, and writing either would
   * strand the record, because withdrawal deleted the membership document
   * that grants event access and nothing here re-creates it.
   */
  function isEditFrozen(field: FieldSpec): boolean {
    if (!editing) return false;
    if (field.kind !== "select" || !field.options) return false;
    const value = String(spec.toForm(editing)[field.name] ?? "");
    return value !== "" && !field.options.some((o) => o.value === value);
  }

  const linkSource = spec.linkFirst
    ? lookups?.[spec.linkFirst.field]
    : undefined;
  /** False while a new record waits for the organiser to link it or to ask
   *  for the fields. An edit always shows them, and so does a roster that has
   *  nobody left to link to (empty, unreadable, or everyone already linked),
   *  since then typing is the only way. */
  const detailsVisible =
    !spec.linkFirst ||
    editing !== null ||
    detailsOpen ||
    Boolean(String(form[spec.linkFirst.field] ?? "").trim()) ||
    (linkSource !== undefined &&
      !linkSource.loading &&
      (linkSource.error !== undefined ||
        linkSource.options.every(
          (option) =>
            linkSource.unavailable?.(option, null, form) !== undefined,
        )));

  /** Mirror the rules so a rejection arrives as a sentence, not a denial. */
  function firstProblem(): string | null {
    if (!detailsVisible && spec.linkFirst)
      return `Pick someone from the roster, or choose "${spec.linkFirst.manualLabel}".`;
    for (const field of spec.fields) {
      // An edit neither renders a create-only field nor sends it, so
      // validating one blocks the form over an input that is not on screen.
      // Attendee `email` is the case: it is required on create and absent
      // from every update key list.
      if (editing && field.createOnly) continue;
      const value = form[field.name];
      if (field.kind === "switch" || field.kind === "select") continue;
      if (field.kind === "image") {
        // No pattern: whatever URL is stored already renders, and a new value
        // only ever comes from an upload.
        if (
          field.required &&
          !files[field.name] &&
          !String(form[field.name] ?? "").trim()
        )
          return `${field.label} is required.`;
        continue;
      }
      if (field.kind === "number" || field.kind === "order") {
        const numeric = Number(value);
        if (!Number.isInteger(numeric))
          return `${field.label} must be a whole number.`;
        if (field.min != null && numeric < field.min)
          return `${field.label} must be at least ${field.min}.`;
        if (field.max != null && numeric > field.max)
          return `${field.label} must be at most ${field.max}.`;
        continue;
      }
      const text = String(value ?? "").trim();
      if (field.required && !text) return `${field.label} is required.`;
      if (field.maxLength && text.length > field.maxLength)
        return `${field.label} must be ${field.maxLength} characters or fewer.`;
      // Blank is exempt: clearWhenBlank removes the field, and the rules accept
      // absent. Only a non-empty value has to match.
      if (text && field.pattern && !field.pattern.test(text))
        return field.patternMessage ?? `${field.label} is not valid.`;
      if (field.kind === "color" && text && !/^#[0-9a-fA-F]{6}$/.test(text))
        return `${field.label} must be a colour like #1d4ed8.`;
    }
    return null;
  }

  /**
   * Optional text is omitted rather than sent empty: several of these fields
   * are validated with isBoundedString, which requires a non-empty string, so
   * an untouched input would otherwise turn into a refused write.
   *
   * An image field is sent on an update only when the form changed it. The
   * dialog holds the URL it was opened with, and somebody may have replaced
   * the picture since, after which the backend deletes the old file. Sending
   * the untouched value back would point the record at that deleted file.
   */
  function buildPayload(forCreate: boolean): EntityFields {
    const payload: EntityFields = {};
    for (const field of spec.fields) {
      if (!forCreate && field.createOnly) continue;
      if (!forCreate && isEditFrozen(field)) continue;
      if (!forCreate && field.kind === "image" && !imageChanged(field))
        continue;
      const value = form[field.name];
      if (field.storedAs) {
        const text = String(value ?? "").trim();
        const separator = text.indexOf(":");
        const chosenKey = separator > 0 ? text.slice(0, separator) : "";
        const chosenId = separator > 0 ? text.slice(separator + 1) : "";
        for (const key of field.storedAs) {
          if (key === chosenKey && chosenId) payload[key] = chosenId;
          // deleteField() is an error in a create, which omits the key.
          else if (!forCreate) payload[key] = deleteField();
        }
        continue;
      }
      if (field.kind === "switch") {
        payload[field.name] = Boolean(value);
      } else if (field.kind === "number" || field.kind === "order") {
        payload[field.name] = Number(value);
      } else {
        const text = String(value ?? "").trim();
        if (text) payload[field.name] = text;
        else if (field.clearWhenBlank)
          // Only on update: deleteField() in a create is an error, and create
          // simply omits the key instead.
          payload[field.name] = forCreate ? undefined : deleteField();
        else if (!forCreate || field.alwaysSend) payload[field.name] = "";
        if (payload[field.name] === undefined) delete payload[field.name];
      }
    }
    return payload;
  }

  async function submit() {
    const problem = firstProblem();
    setInvalid(problem);
    setUploadLeftover(null);
    if (problem) return;

    if (!editing && spec.createCommand) {
      // The dialog stays open until the TRIGGER reports, not until submit()
      // resolves: submit() returns once the command document is written, which
      // is before the attendee exists. Closing there would show the list
      // without the new row, and would hide the duplicate-address refusal that
      // is the point of routing creation through a command at all.
      awaitingCreate.current = true;
      await command.submit(
        spec.createCommand.type,
        eventId,
        uid,
        buildPayload(true),
      );
      return;
    }

    // The id comes first when there is a file to upload, because the file's
    // path carries it and the rules match on that path.
    const entityId =
      editing?.id ??
      (imageFields.length > 0
        ? doc(collection(db, "events", eventId, spec.collection)).id
        : undefined);
    const payload = buildPayload(!editing);

    const uploaded: string[] = [];
    const chosen = imageFields.filter((field) => files[field.name]);
    if (chosen.length > 0 && entityId) {
      setUploading(true);
      try {
        for (const field of chosen) {
          const result = await uploadEntityImage(
            eventId,
            spec.collection,
            entityId,
            files[field.name] as File,
          );
          uploaded.push(result.path);
          payload[field.name] = result.url;
        }
      } catch (cause) {
        setUploading(false);
        // Nothing points at what did upload, so it goes.
        setUploadLeftover(await discardUploads(uploaded));
        setInvalid(
          `The image could not be uploaded, so nothing was saved. ${cause instanceof Error ? cause.message : ""}`.trim(),
        );
        return;
      }
      setUploading(false);
    }

    const ok = editing
      ? await writes.update(editing.id, payload, {
          timestampFields: spec.updateTimestamps,
        })
      : await writes.create(payload, {
          timestampFields: spec.createTimestamps ?? ["createdAt"],
          id: entityId,
        });

    if (!ok) {
      // The document still points at its old image, so the new upload is
      // the one nothing references.
      setUploadLeftover(await discardUploads(uploaded));
      return;
    }

    // The replaced file, if any, is the backend's to delete: it waits until
    // the sessions a speaker presents show the new photo.
    setOpen(false);
    reload();
  }

  async function confirmDelete(item: T) {
    if (spec.deleteCommand) {
      await command.submit(spec.deleteCommand.type, eventId, uid, {
        [spec.deleteCommand.payloadKey]: item.id,
      });
      return;
    }
    const ok = await writes.remove(item.id);
    setPendingDelete(null);
    if (ok) reload();
  }

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle || !spec.searchFields) return items;
    return items.filter((item) =>
      spec.searchFields!(item).some((value) =>
        (value ?? "").toLowerCase().includes(needle),
      ),
    );
  }, [items, search, spec]);

  // The linked field leads when the spec asks for it; the rest keep their
  // declared order.
  const orderedFields = spec.linkFirst
    ? [
        ...spec.fields.filter((field) => field.name === spec.linkFirst?.field),
        ...spec.fields.filter((field) => field.name !== spec.linkFirst?.field),
      ]
    : spec.fields;

  /** Whether the form replaced or removed the image the record held when
   *  the dialog opened. Always true on a create, which has nothing to keep. */
  function imageChanged(field: FieldSpec): boolean {
    if (!editing) return true;
    const original = String(spec.toForm(editing)[field.name] ?? "").trim();
    return (
      files[field.name] !== undefined ||
      String(form[field.name] ?? "").trim() !== original
    );
  }

  /** The way back to the image the document holds, offered once the form
   *  has replaced or removed it. A new record has no image to go back to, and
   *  Remove already clears a chosen file. */
  function imageUndoLabel(field: FieldSpec): string | undefined {
    if (!editing) return undefined;
    const original = String(spec.toForm(editing)[field.name] ?? "");
    return original && imageChanged(field)
      ? "Keep the current image"
      : undefined;
  }

  const bulkNoun = { singular: spec.singular, plural: spec.plural };
  const bulkItems = visible.map((item) => ({
    id: item.id,
    label: spec.primary(item),
  }));
  const selecting = bulkEnabled && bulk.selecting;

  if (loading) return <LoadingCards cards={3} />;
  if (error)
    return (
      <EmptyState icon={AlertCircle} title={`Could not load ${spec.plural}`}>
        {error}
      </EmptyState>
    );

  return (
    <div className="space-y-4" data-testid={`${spec.testId}-content`}>
      <div className="flex items-center justify-between gap-4">
        <Badge variant="outline">
          {visible.length} {visible.length === 1 ? spec.singular : spec.plural}
        </Badge>
        <div className="flex flex-wrap justify-end gap-2">
          {bulkEnabled && !selecting && items.length > 0 && (
            <BulkSelectButton
              onClick={bulk.enter}
              testId={`select-${spec.testId}`}
            />
          )}
          <Button
            size="sm"
            variant="cta"
            onClick={startCreate}
            data-testid={`add-${spec.testId}`}
          >
            <Plus className="h-4 w-4" />
            Add {spec.singular}
          </Button>
        </div>
      </div>

      {spec.searchFields && (
        <Input
          testId={`${spec.testId}-search`}
          placeholder={`Search ${spec.plural}`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}

      {selecting && (
        <BulkSelectBar
          bulk={bulk}
          visible={bulkItems}
          noun={bulkNoun}
          busy={commandBusy || writes.saving}
          testId={`bulk-${spec.testId}`}
        />
      )}

      {!selecting && spec.bulkAction?.(visible)}

      {/* This strip carries every command outcome for the section, and since
          attendees create through a command it is no longer only deletion. */}
      <AdminCommandFeedback
        state={command.state}
        onReset={command.reset}
        testId={`${spec.testId}-command`}
      />
      {writes.error && (
        <p
          className="text-sm text-destructive-text"
          data-testid={`${spec.testId}-error`}
        >
          {writes.error}
        </p>
      )}

      {visible.length === 0 ? (
        <EmptyState icon={spec.icon} title={`No ${spec.plural}`}>
          {search.trim() ? "Nothing matches that search." : "Nothing here yet."}
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {visible.map((item) => (
            <Card key={item.id} className="border-border shadow-none">
              {/* Below sm the actions take a row of their own under the
                  name. Beside it, a track picker and two icon buttons left
                  a 375px phone about seven characters of name and pushed
                  the delete button off a 320px screen. */}
              <CardContent className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3">
                {selecting && (
                  <BulkRowCheckbox
                    checked={bulk.selected.has(item.id)}
                    onChange={() => bulk.toggle(item.id)}
                    name={spec.primary(item)}
                    testId={`select-${spec.testId}-${item.id}`}
                  />
                )}
                {spec.thumbnail && (
                  <Thumbnail
                    src={spec.thumbnail(item)}
                    testId={`${spec.testId}-thumb-${item.id}`}
                  />
                )}
                <div className="min-w-0 flex-1">
                  {/* Wrapped on phones, where the name has the row to
                      itself: a long signal label cut to an ellipsis could
                      not be read anywhere else on the tab. */}
                  <p
                    className="font-medium text-card-foreground wrap-anywhere sm:truncate"
                    data-testid={`${spec.testId}-name-${item.id}`}
                  >
                    {spec.primary(item)}
                  </p>
                  {spec.secondary?.(item) && (
                    <p className="truncate text-sm text-muted-foreground">
                      {spec.secondary(item)}
                    </p>
                  )}
                </div>
                {!selecting && (
                  <div className="flex shrink-0 items-center gap-1 max-sm:basis-full max-sm:justify-end">
                    {spec.rowAction?.(item)}
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Edit ${spec.primary(item)}`}
                      data-testid={`edit-${spec.testId}-${item.id}`}
                      onClick={() => startEdit(item)}
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={commandBusy || writes.saving}
                      aria-label={`Delete ${spec.primary(item)}`}
                      data-testid={`delete-${spec.testId}-${item.id}`}
                      onClick={() => setPendingDelete(item)}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {spec.editor && open && (
        <Fragment key={editing?.id ?? "new"}>
          {spec.editor({
            item: editing,
            onClose: () => setOpen(false),
            onSaved: reload,
          })}
        </Fragment>
      )}

      <Dialog open={open && !spec.editor} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? `Edit ${spec.singular}` : `Add ${spec.singular}`}
            </DialogTitle>
          </DialogHeader>

          <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
            {!editing && spec.createFinder && (
              <>
                {spec.createFinder({
                  eventId,
                  uid,
                  disabled: commandBusy,
                  onPick: (fields) => {
                    // Only fields this form has, so a finder cannot write a
                    // key the command would refuse.
                    const known = new Set(spec.fields.map((f) => f.name));
                    setForm((current) => ({
                      ...current,
                      ...Object.fromEntries(
                        Object.entries(fields).filter(([name]) =>
                          known.has(name),
                        ),
                      ),
                    }));
                    setInvalid(null);
                  },
                })}
                <p
                  className="border-t border-border pt-3 text-sm font-medium text-foreground"
                  data-testid={`${spec.testId}-manual-details`}
                >
                  Or enter their details yourself
                </p>
              </>
            )}
            {orderedFields.map((field) => {
              if (field.hidden) return null;
              if (!detailsVisible && field.name !== spec.linkFirst?.field)
                return null;
              if (editing && field.createOnly) return null;
              if (isEditFrozen(field)) return null;
              const inputId = `${spec.testId}-${field.name}`;
              return (
                <div key={field.name} className="space-y-1.5">
                  <Label htmlFor={inputId}>
                    {field.label}
                    {field.required && " *"}
                  </Label>
                  {field.kind === "switch" ? (
                    <div>
                      <Switch
                        checked={Boolean(form[field.name])}
                        aria-label={field.label}
                        data-testid={inputId}
                        onCheckedChange={(next) =>
                          setForm({ ...form, [field.name]: next })
                        }
                      />
                    </div>
                  ) : field.kind === "select" ? (
                    <Select
                      value={String(form[field.name] ?? "")}
                      onValueChange={(next) =>
                        setForm({ ...form, [field.name]: next })
                      }
                    >
                      <SelectTrigger className="w-full" testId={inputId}>
                        <SelectValue placeholder={field.placeholder} />
                      </SelectTrigger>
                      <SelectContent>
                        {(field.options ?? []).map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : field.kind === "lookup" ? (
                    <LookupField
                      testId={inputId}
                      source={
                        lookups?.[field.name] ?? {
                          options: [],
                          loading: false,
                          empty: "Nothing to link to.",
                        }
                      }
                      editingId={editing?.id ?? null}
                      form={form}
                      value={String(form[field.name] ?? "")}
                      onPick={(option) => {
                        const next: FormValues = {
                          ...form,
                          [field.name]: option.id,
                        };
                        for (const target of field.fills ?? []) {
                          const filled = option.fills?.[target];
                          if (filled && !String(next[target] ?? "").trim())
                            next[target] = filled;
                        }
                        setForm(next);
                        // Unlinking again keeps what the link filled in.
                        if (field.name === spec.linkFirst?.field)
                          setDetailsOpen(true);
                      }}
                      onClear={() => setForm({ ...form, [field.name]: "" })}
                    />
                  ) : field.kind === "image" &&
                    !editing &&
                    spec.createCommand ? (
                    // The upload path carries the record's id, and a command
                    // create learns its id only from the trigger. So the
                    // photo waits for the record to exist.
                    <p
                      className="text-xs text-muted-foreground"
                      data-testid={`${inputId}-after-create`}
                    >
                      Save the {spec.singular} first, then open them from the
                      list to upload a photo.
                    </p>
                  ) : field.kind === "image" ? (
                    <EntityImageField
                      id={inputId}
                      testId={inputId}
                      value={String(form[field.name] ?? "")}
                      file={files[field.name]}
                      required={Boolean(field.required)}
                      shape={field.image?.shape ?? "logo"}
                      maxBytes={field.image?.maxBytes ?? 5 * 1024 * 1024}
                      disabled={uploading || writes.saving}
                      onFile={(file) =>
                        setFiles({ ...files, [field.name]: file })
                      }
                      onRemove={() => {
                        const { [field.name]: _dropped, ...rest } = files;
                        setFiles(rest);
                        setForm({ ...form, [field.name]: "" });
                      }}
                      onUndo={() => {
                        const { [field.name]: _dropped, ...rest } = files;
                        setFiles(rest);
                        setForm({
                          ...form,
                          [field.name]: editing
                            ? (spec.toForm(editing)[field.name] ?? "")
                            : "",
                        });
                      }}
                      undoLabel={imageUndoLabel(field)}
                    />
                  ) : field.kind === "company" ? (
                    <CompanyField
                      id={inputId}
                      testId={inputId}
                      value={String(form[field.name] ?? "")}
                      onChange={(next) =>
                        setForm({ ...form, [field.name]: next })
                      }
                    />
                  ) : field.kind === "order" ? (
                    <OrderField
                      testId={inputId}
                      value={Number(form[field.name] ?? 0)}
                      min={field.min ?? 0}
                      max={field.max}
                      onChange={(next) =>
                        setForm({ ...form, [field.name]: next })
                      }
                    />
                  ) : field.kind === "color" ? (
                    <Input
                      id={inputId}
                      testId={inputId}
                      type="color"
                      value={String(form[field.name] ?? "")}
                      onChange={(e) =>
                        setForm({ ...form, [field.name]: e.target.value })
                      }
                      className="h-11 w-20 cursor-pointer p-1"
                    />
                  ) : field.kind === "textarea" ? (
                    <Textarea
                      id={inputId}
                      data-testid={inputId}
                      rows={4}
                      value={String(form[field.name] ?? "")}
                      placeholder={field.placeholder}
                      onChange={(e) =>
                        setForm({ ...form, [field.name]: e.target.value })
                      }
                    />
                  ) : (
                    <Input
                      id={inputId}
                      testId={inputId}
                      type={field.kind === "number" ? "number" : "text"}
                      value={String(form[field.name] ?? "")}
                      placeholder={field.placeholder}
                      onChange={(e) =>
                        setForm({ ...form, [field.name]: e.target.value })
                      }
                    />
                  )}
                  {field.help && (
                    <p className="text-xs text-muted-foreground">
                      {field.help}
                    </p>
                  )}
                  {!detailsVisible && field.name === spec.linkFirst?.field && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      // Wraps rather than overflowing a 320px dialog, and
                      // meets the 44px tap target on a phone.
                      className="h-auto min-h-9 max-w-full whitespace-normal py-2 text-left max-sm:min-h-11"
                      data-testid={`${spec.testId}-enter-details`}
                      onClick={() => setDetailsOpen(true)}
                    >
                      {spec.linkFirst.manualLabel}
                    </Button>
                  )}
                </div>
              );
            })}

            {/* The command's refusal belongs here, beside the field that
                caused it, not only in the strip behind the dialog. */}
            {(invalid || writes.error || commandError) && (
              <p
                className="text-sm text-destructive-text"
                data-testid={`${spec.testId}-form-error`}
              >
                {invalid ?? writes.error ?? commandError}
              </p>
            )}
            {uploadLeftover && (
              <p
                role="alert"
                className="text-sm text-destructive-text"
                data-testid={`${spec.testId}-upload-leftover`}
              >
                {uploadLeftover}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="cta"
              onClick={submit}
              disabled={writes.saving || commandBusy || uploading}
              data-testid={`save-${spec.testId}`}
            >
              {uploading
                ? "Uploading…"
                : writes.saving || commandBusy
                  ? "Saving…"
                  : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {bulkEnabled && (
        <BulkDeleteDialog
          bulk={bulk}
          noun={bulkNoun}
          consequences={
            spec.deleteClearsReferences ? CLEARS_REFERENCES : undefined
          }
          testId={`bulk-delete-${spec.testId}`}
        />
      )}

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(next) => !next && setPendingDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {spec.singular}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {pendingDelete ? spec.primary(pendingDelete) : ""} will be removed.
            {spec.deleteCommand
              ? " References to it elsewhere in the event are cleared first, and the identifier cannot be reused afterwards."
              : ""}
            {spec.deleteClearsReferences ? ` ${CLEARS_REFERENCES}` : ""}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={commandBusy || writes.saving}
              data-testid={`confirm-delete-${spec.testId}`}
              onClick={() => pendingDelete && confirmDelete(pendingDelete)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Display order, as a pair of arrows rather than a number to type.
 *
 * Arrows rather than drag and drop because ordering is edited one record at a
 * time, inside this dialog. A drag reorders a list, so it would have to
 * renumber every record the dragged one passed — several writes with no
 * transaction behind them, on a list where every record written before
 * `displayOrder` existed still sits at 0 and the first drag would therefore
 * have to renumber all of it. Arrows change one number in one document, and
 * they work on a phone, which is where an organiser runs this during an event.
 */
function OrderField({
  testId,
  value,
  min,
  max,
  onChange,
}: {
  testId: string;
  value: number;
  min: number;
  max?: number;
  onChange: (next: number) => void;
}) {
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      <Button
        variant="outline"
        size="icon"
        aria-label="Move earlier"
        disabled={value <= min}
        data-testid={`${testId}-earlier`}
        onClick={() => onChange(value - 1)}
      >
        <ChevronUp className="h-4 w-4" />
      </Button>
      <span
        className="w-10 text-center text-sm tabular-nums text-foreground"
        data-testid={`${testId}-value`}
      >
        {value}
      </span>
      <Button
        variant="outline"
        size="icon"
        aria-label="Move later"
        disabled={max != null && value >= max}
        data-testid={`${testId}-later`}
        onClick={() => onChange(value + 1)}
      >
        <ChevronDown className="h-4 w-4" />
      </Button>
    </div>
  );
}

/** At most this many matches are listed; narrowing the search is the way past
 *  it. An event's roster runs to hundreds, and a list that long is not a
 *  picker. */
const MAX_LOOKUP_RESULTS = 8;

/**
 * A search box over another collection, resolving to one document id.
 *
 * Not a `select`: the roster it is pointed at is hundreds of rows, and a
 * dropdown of hundreds is unusable on a phone and unreadable anywhere.
 *
 * The form holds the id, not a position in the list, which is what makes the
 * control safe when the options cannot be loaded: an existing link still shows
 * as a chip, still saves, and only the search half is unavailable. A picker
 * that derived its value from the list would quietly write a blank instead.
 */
function LookupField({
  testId,
  source,
  editingId,
  form,
  value,
  onPick,
  onClear,
}: {
  testId: string;
  source: LookupSource;
  editingId: string | null;
  form: FormValues;
  value: string;
  onPick: (option: LookupOption) => void;
  onClear: () => void;
}) {
  const [search, setSearch] = useState("");

  if (value) {
    const linked = source.options.find((option) => option.id === value);
    return (
      <div
        className="flex items-center gap-2 rounded-xl border border-border px-3 py-2"
        data-testid={`${testId}-linked`}
      >
        <span
          className="min-w-0 flex-1 truncate text-sm text-foreground"
          data-testid={
            !linked && !source.loading && source.missing
              ? `${testId}-missing`
              : undefined
          }
        >
          {/* The id, when the list is still loading or the row has gone. It is
              the only honest thing to show, and it is what the organiser needs
              to see before deciding to unlink. A source that can say more
              (`missing`) says it instead once the list has loaded. */}
          {linked
            ? linked.label
            : !source.loading && source.missing
              ? source.missing
              : value}
          {linked?.detail && (
            <span className="text-muted-foreground"> · {linked.detail}</span>
          )}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="max-sm:h-11"
          data-testid={`${testId}-clear`}
          onClick={onClear}
        >
          Unlink
        </Button>
      </div>
    );
  }

  if (source.loading)
    return <p className="text-xs text-muted-foreground">Loading…</p>;
  if (source.error)
    return (
      <p
        className="text-xs text-destructive-text"
        data-testid={`${testId}-error`}
      >
        {source.error}
      </p>
    );
  if (source.options.length === 0)
    return <p className="text-xs text-muted-foreground">{source.empty}</p>;

  const needle = search.trim().toLowerCase();
  const matches = source.options
    .filter(
      (option) =>
        !needle ||
        `${option.label} ${option.detail ?? ""}`.toLowerCase().includes(needle),
    )
    .slice(0, MAX_LOOKUP_RESULTS);

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Input
        testId={`${testId}-search`}
        placeholder="Search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {matches.length === 0 ? (
        <p className="text-xs text-muted-foreground">{source.empty}</p>
      ) : (
        <ul className="max-h-48 space-y-1 overflow-y-auto">
          {matches.map((option) => {
            const reason = source.unavailable?.(option, editingId, form);
            return (
              <li key={option.id}>
                <button
                  type="button"
                  disabled={reason !== undefined}
                  data-testid={`${testId}-option-${option.id}`}
                  onClick={() => onPick(option)}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent max-sm:min-h-11"
                >
                  <span className="min-w-0 flex-1 truncate">
                    {option.label}
                    {option.detail && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {option.detail}
                      </span>
                    )}
                  </span>
                  {reason && (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {reason}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** A row's picture, or a blank square that keeps the rows aligned. */
function Thumbnail({ src, testId }: { src?: string; testId: string }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  return src && !broken ? (
    <img
      src={src}
      alt=""
      loading="lazy"
      className="h-10 w-10 shrink-0 rounded-md bg-muted object-contain"
      data-testid={testId}
      onError={() => setBroken(true)}
    />
  ) : (
    <div className="h-10 w-10 shrink-0 rounded-md bg-muted" aria-hidden />
  );
}
